import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  AccountActionsEnum,
  LoginAccountByEmail,
  LoginAccountByFacebook,
  LoginAccountByGoogle,
  LoginAccountError,
  LoginAccountSuccess,
  RegisterAccountByEmail,
  RegisterAccountByFacebook,
  RegisterAccountByGoogle,
  RegisterAccountError,
  RegisterAccountSuccess,
  setTrackingObject,
  SetTrackingObject,
  TrackEvent,
  trackEvent,
} from '../actions';
import { AppActionsEnum } from '../../../app/store/actions';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import { concat, Observable } from 'rxjs';
import { flatMap, map, switchMap, take, tap } from 'rxjs/operators';
import { pickBy } from 'lodash';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
} from 'i2g-ng-tracking';
import { AccountStore } from '../stores';
import { AppStore } from '../../../app/store/stores';
import { AccountTrackingObject } from '../../../tracking/objects/account-tracking-object.model';

@Injectable()
export class TrackingEffects {
  constructor(
    private _actions$: Actions,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _accountStore: AccountStore,
    private _appStore: AppStore,
    private _trackingService: I2gNgTrackingService
  ) {}

  @Effect()
  initApp(): Observable<SetTrackingObject> {
    return this._actions$.pipe(
      ofType(AppActionsEnum.INIT_APP),
      map(() => setTrackingObject(this._trackingObjectBuilder.buildAccount()))
    );
  }

  @Effect({ dispatch: false })
  trackEvent() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.TRACK_EVENT),
      switchMap(({ payload }: TrackEvent) => {
        return this._accountStore.getTrackingObject().pipe(
          take(1),
          tap((trackingObject: AccountTrackingObject) => {
            return this._trackingService.trackEvent(
              trackingObject,
              payload.trackingAction
            );
          })
        );
      })
    );
  }

  @Effect()
  registerAccountByEmail(): Observable<SetTrackingObject | TrackEvent> {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_EMAIL),
      flatMap(({ payload }: RegisterAccountByEmail) => {
        return concat([
          setTrackingObject(
            this._trackingObjectBuilder.buildAccount({ email: payload.email })
          ),
          trackEvent(
            this._trackingActionBuilder.buildSignUpStarted(
              payload.trackingScreenName
            )
          ),
        ]);
      })
    );
  }

  @Effect()
  registerAccountByFacebook(): Observable<TrackEvent> {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_FACEBOOK),
      map(({ payload }: RegisterAccountByFacebook) =>
        trackEvent(
          this._trackingActionBuilder.buildSignUpStarted(
            payload.trackingScreenName
          )
        )
      )
    );
  }

  @Effect()
  registerAccountByGoogle(): Observable<TrackEvent> {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_GOOGLE),
      map(({ payload }: RegisterAccountByGoogle) =>
        trackEvent(
          this._trackingActionBuilder.buildSignUpStarted(
            payload.trackingScreenName
          )
        )
      )
    );
  }

  @Effect()
  registerAccountSuccess() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_SUCCESS),
      flatMap(({ payload }: RegisterAccountSuccess) => {
        return this._accountStore.getUserEmail().pipe(
          take(1),
          flatMap((email: string) => {
            const { userId, accountId } = payload.account;
            const uniqueId = this._trackingService.generateUniqueId(
              userId,
              accountId
            );
            const traits = pickBy({
              email,
              accountId,
              i2gUserId: userId,
            });
            this._trackingService.identify(uniqueId, traits);

            return concat([
              setTrackingObject(
                this._trackingObjectBuilder.buildAccount(traits)
              ),
              trackEvent(
                this._trackingActionBuilder.buildSignUpCompleted(
                  payload.trackingScreenName
                )
              ),
            ]);
          })
        );
      })
    );
  }

  @Effect()
  registerAccountError() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_ERROR),
      flatMap(({ payload }: RegisterAccountError) => {
        const errorCode =
          payload.error instanceof Error ? null : payload.error.code;
        return concat([
          trackEvent(
            this._trackingActionBuilder.buildSignUpFailed(
              errorCode,
              payload.trackingScreenName
            )
          ),
          setTrackingObject(this._trackingObjectBuilder.buildAccount()),
        ]);
      })
    );
  }

  @Effect()
  loginAccountByEmail(): Observable<SetTrackingObject | TrackEvent> {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_EMAIL),
      flatMap(({ payload }: LoginAccountByEmail) => {
        return concat([
          setTrackingObject(
            this._trackingObjectBuilder.buildAccount({ email: payload.email })
          ),
          trackEvent(
            this._trackingActionBuilder.buildSignInStarted(
              payload.trackingScreenName
            )
          ),
        ]);
      })
    );
  }

  @Effect()
  loginAccountByFacebook(): Observable<TrackEvent> {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_FACEBOOK),
      map(({ payload }: LoginAccountByFacebook) =>
        trackEvent(
          this._trackingActionBuilder.buildSignInStarted(
            payload.trackingScreenName
          )
        )
      )
    );
  }

  @Effect()
  loginAccountByGoogle(): Observable<TrackEvent> {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_FACEBOOK),
      map(({ payload }: LoginAccountByGoogle) =>
        trackEvent(
          this._trackingActionBuilder.buildSignInStarted(
            payload.trackingScreenName
          )
        )
      )
    );
  }

  @Effect()
  loginAccountError() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_ERROR),
      flatMap(({ payload }: LoginAccountError) => {
        const errorCode =
          payload.error instanceof Error ? null : payload.error.code;
        return concat([
          trackEvent(
            this._trackingActionBuilder.buildSignInFailed(
              errorCode,
              payload.trackingScreenName
            )
          ),
          setTrackingObject(this._trackingObjectBuilder.buildAccount()),
        ]);
      })
    );
  }

  @Effect()
  loginAccountSuccess() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_SUCCESS),
      flatMap(({ payload }: LoginAccountSuccess) => {
        return this._accountStore.getUserEmail().pipe(
          take(1),
          flatMap((email: string) => {
            const userId = payload.userAccount.userId();
            const accountId = payload.accountId;
            const traits = pickBy({
              email,
              accountId,
              i2gUserId: userId,
            });

            const uniqueId = this._trackingService.generateUniqueId(
              userId,
              accountId
            );
            this._trackingService.identify(uniqueId, traits);

            return concat([
              setTrackingObject(
                this._trackingObjectBuilder.buildAccount(traits)
              ),
              trackEvent(
                this._trackingActionBuilder.buildSignInCompleted(
                  payload.trackingScreenName
                )
              ),
            ]);
          })
        );
      })
    );
  }
}
